/**
 * Live calls — the ringing cards' state (dynamic call routing).
 *
 * A module-level store, like the tone-alert queue: the socket hook feeds it
 * (CALL_ROUTING_RINGING / CALL_ROUTING_ENDED), the host renders it, and a
 * page that loads mid-ring fills it from GET …/call-routing/ringing. A ring
 * disappears when the post-call report ends it, when the person dismisses
 * it, or after RING_TTL_MS without news.
 */
import { useSyncExternalStore } from "react";

export interface LiveCall {
  tenantId: number;
  callKey: string;
  sourceId: number;
  sourceName: string | null;
  from: string;
  did: string;
  userId: number | null;
  userName: string | null;
  destination: string | null;
  reason: string;
  leadId: number | null;
  leadName: string | null;
  leadCreated: boolean;
  branchId: number | null;
  at: string;
}

export interface LiveCallEnd {
  callKey: string;
  status: string;
  duration: number;
  callLogId: number | null;
}

export const RING_TTL_MS = 60_000;

let calls: LiveCall[] = [];
let dismissed = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

const prune = () => {
  const cutoff = Date.now() - RING_TTL_MS;
  const next = calls.filter((c) => new Date(c.at).getTime() >= cutoff && !dismissed.has(c.callKey));
  if (next.length !== calls.length) {
    calls = next;
    notify();
  }
};

export function ringCall(call: LiveCall) {
  if (dismissed.has(call.callKey)) return;
  const others = calls.filter((c) => c.callKey !== call.callKey);
  calls = [call, ...others];
  notify();
}

export function endCall(end: LiveCallEnd) {
  const before = calls.length;
  calls = calls.filter((c) => c.callKey !== end.callKey);
  if (calls.length !== before) notify();
}

export function dismissCall(callKey: string) {
  dismissed.add(callKey);
  if (dismissed.size > 200) dismissed = new Set([...dismissed].slice(-100));
  endCall({ callKey, status: "dismissed", duration: 0, callLogId: null });
}

/** Replace the list from the server (page load / reconnect), keeping local dismissals. */
export function seedCalls(list: LiveCall[]) {
  calls = list.filter((c) => !dismissed.has(c.callKey)).sort((a, b) => (a.at < b.at ? 1 : -1));
  notify();
  prune();
}

setInterval(prune, 5_000);

export function useLiveCalls(): LiveCall[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => calls,
    () => calls,
  );
}
